import React from "react"
import {Box, Chip, Typography} from "@mui/material"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import {formatDistanceToNow} from "date-fns"

import type {UnidentifiedMessage, UnidentifiedStatus} from "../types"

import {colors} from "@indebted/theme/colors"

const deferralReasonLabels: Record<string, string> = {
	MultipleCandidates: "Multiple matches",
	NoMatch: "No match",
	CrossRegionCandidates: "Matched in both regions",
}

const amberTint = {
	bgcolor: colors.warning["50"],
	borderColor: colors.warning["200"],
	color: colors.warning["800"],
	fontWeight: 500,
}

const tealTint = {
	bgcolor: colors.tiffanyBlue["50"],
	borderColor: colors.tiffanyBlue["900"],
	color: "#0F5C57",
	fontWeight: 500,
}

const grayTint = {
	bgcolor: colors.gray["50"],
	borderColor: colors.gray["300"],
	color: colors.gray["700"],
}

function DeferralReasonChip({reason}: {reason: string}) {
	return (
		<Chip
			label={deferralReasonLabels[reason] ?? reason}
			size="small"
			variant="outlined"
			sx={reason === "CrossRegionCandidates" ? tealTint : amberTint}
		/>
	)
}

function SourceChip({tag, title}: {tag: string; title: string}) {
	return <Chip label={tag} title={title} size="small" variant="outlined" sx={grayTint} />
}

function StatusChip({status, message}: {status: UnidentifiedStatus; message: UnidentifiedMessage}) {
	if (status === "assigned") return <Chip label="Assigned" size="small" variant="outlined" color="success" />
	if (status === "dismissed") {
		const reason = message.Resolution?.Status === "dismissed" ? message.Resolution.Reason : ""
		return (
			<Chip
				label={reason ? `Dismissed · ${reason}` : "Dismissed"}
				size="small"
				variant="outlined"
				sx={grayTint}
			/>
		)
	}
	return null
}

function ReceivedAgo({receivedAt}: {receivedAt: string}) {
	return (
		<Box display="flex" alignItems="center" gap={1} color="text.secondary">
			<AccessTimeIcon sx={{fontSize: 20}} />
			<Typography variant="body2" component="span" color="textSecondary">
				<Box component="span" color={colors.gray["700"]}>
					Received
				</Box>{" "}
				{formatDistanceToNow(new Date(receivedAt), {addSuffix: true})}
			</Typography>
		</Box>
	)
}

export {DeferralReasonChip, ReceivedAgo, SourceChip, StatusChip}
